import { useState, useEffect } from "react";
import { Search, FileText, Loader, CheckCircle, Image, Video, File, Type } from "lucide-react";
import api from "../../services/api";
import { toast } from "react-toastify";
import { catColor } from "./constants";

const HEADER_ICON = { image: Image, video: Video, document: File, text: Type };

const countParams = (text = "") => {
  const set = new Set((text.match(/\{\{\d+\}\}/g) || []).map((m) => parseInt(m.replace(/\D/g, ""))));
  return set.size;
};

const StepTemplate = ({ selected, onSelect, bodyParams, onParamsChange, onMetaBodyChange }) => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [search, setSearch]       = useState("");
  const [metaBody, setMetaBody]   = useState(null);
  const [metaLoading, setMetaLoading] = useState(false);

  useEffect(() => {
    api.get("/getTemplate")
      .then((r) => {
        const approved = (r.data.templates || []).filter(
          (t) => t.status?.toLowerCase() === "approved"
        );
        setTemplates(approved);
      })
      .catch(() => toast.error("Failed to load templates"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!selected) { setMetaBody(null); onMetaBodyChange?.(null); return; }
    setMetaLoading(true);
    api.get(`/message-templates/meta/${encodeURIComponent(selected.templateName)}`)
      .then((r) => {
        const body = r.data.template?.components?.find((c) => c.type === "BODY")?.text || null;
        setMetaBody(body);
        onMetaBodyChange?.(body);
        if (body) {
          const n = countParams(body);
          onParamsChange(Array.from({ length: n }, (_, i) => bodyParams[i] || ""));
        }
      })
      .catch(() => { setMetaBody(null); onMetaBodyChange?.(null); })
      .finally(() => setMetaLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected?.id]);

  const filtered = templates.filter((t) =>
    t.templateName.toLowerCase().includes(search.toLowerCase())
  );

  const HeaderIcon = ({ type }) => {
    const Icon = HEADER_ICON[type] || null;
    return Icon ? <Icon className="w-3.5 h-3.5 opacity-60" /> : null;
  };

  const paramCount = countParams(metaBody || "");

  return (
    <div>
      <h2 className="text-lg font-semibold text-gray-900 mb-1">Choose a Template</h2>
      <p className="text-sm text-gray-500 mb-4">Select an approved WhatsApp template for this broadcast.</p>

      <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 mb-4">
        <Search className="w-4 h-4 text-gray-400 flex-shrink-0" />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search templates…"
          className="flex-1 text-sm bg-transparent outline-none text-gray-700 placeholder-gray-400"
        />
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader className="w-6 h-6 animate-spin text-gray-400" /></div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <FileText className="w-10 h-10 mx-auto mb-2 opacity-30" />
          <p className="text-sm">{templates.length === 0 ? "No approved templates yet." : "No templates match your search."}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
          {filtered.map((tpl) => (
            <button
              key={tpl.id}
              onClick={() => onSelect(tpl)}
              className={`text-left p-4 rounded-xl border-2 transition-all ${
                selected?.id === tpl.id
                  ? "border-blue-500 bg-blue-50"
                  : "border-gray-200 bg-white hover:border-blue-300 hover:bg-gray-50"
              }`}
            >
              <div className="flex items-start justify-between gap-2">
                <p className="text-sm font-semibold text-gray-900 truncate flex-1">{tpl.templateName}</p>
                {selected?.id === tpl.id && <CheckCircle className="w-4 h-4 text-blue-500 flex-shrink-0" />}
              </div>
              <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${catColor(tpl.category)}`}>
                  {tpl.category}
                </span>
                <span className="text-[10px] text-gray-400">{tpl.language}</span>
                {tpl.headerType && tpl.headerType !== "none" && (
                  <span className="flex items-center gap-0.5 text-[10px] text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded capitalize">
                    <HeaderIcon type={tpl.headerType} />
                    {tpl.headerType}
                  </span>
                )}
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Variable inputs — appear when template has params */}
      {selected && (
        <div className="border-t border-gray-100 pt-4">
          {metaLoading ? (
            <div className="flex items-center gap-2 text-xs text-gray-400">
              <Loader className="w-3.5 h-3.5 animate-spin" /> Loading template variables…
            </div>
          ) : paramCount > 0 ? (
            <div>
              <p className="text-sm font-medium text-gray-700 mb-3">
                Fill in template variables
                <span className="text-xs text-gray-400 font-normal ml-1">({paramCount} required)</span>
              </p>
              <div className="space-y-2">
                {Array.from({ length: paramCount }, (_, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <span className="text-xs text-gray-400 w-10 flex-shrink-0 font-mono">{`{{${i + 1}}}`}</span>
                    <input
                      value={bodyParams[i] || ""}
                      onChange={(e) => {
                        const next = [...bodyParams];
                        next[i] = e.target.value;
                        onParamsChange(next);
                      }}
                      placeholder={`Variable ${i + 1}`}
                      className="flex-1 text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
                    />
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <p className="text-xs text-gray-400 italic">This template has no variables.</p>
          )}
        </div>
      )}
    </div>
  );
};

export default StepTemplate;
